import React, { useEffect, useRef, useState } from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import { useDispatch } from 'react-redux';
import TestWebhookSteps from './TestWebhookSteps';
import TestWebhookError from './TestWebhookError';
import { useMutateTestWebhookHandler } from '../dataHandlers/mutateTestWebhookHandler';
import { showSnackbar } from '../../../api/snackbarSlice';
import appConfig from '../../../config/appConfig';

const TestWebhookRunner = ({ webhookId, tin, name, profileId, setTesting }) => {
  const dispatch = useDispatch();
  const { mutateTestAsync } = useMutateTestWebhookHandler();

  const hasRun = useRef(false);

  const [loadingStep, setLoadingStep] = useState(null);
  const [completed, setCompleted] = useState(false);
  const [failed, setFailed] = useState(false);

  const [steps, setSteps] = useState({
    sendEvent: { done: false, failed: false, status: null, response: null },
    positiveHmac: { done: false, failed: false, status: null, response: null },
    negativeHmac: { done: false, failed: false, status: null, response: null },
    finished: { done: false, failed: false },
  });

  const resetState = () => {
    setCompleted(false);
    setFailed(false);
    setLoadingStep(null);

    setSteps({
      sendEvent: { done: false, failed: false, status: null, response: null },
      positiveHmac: { done: false, failed: false, status: null, response: null },
      negativeHmac: { done: false, failed: false, status: null, response: null },
      finished: { done: false, failed: false },
    });
  };

  const runTest = async () => {
    try {
      setTesting(true);
      resetState();
      setLoadingStep('sendEvent');

      const response = await mutateTestAsync({
        webhook_config_id: webhookId,
        tin: tin,
        profile_id: profileId,
      });

      setSteps((prev) => ({
        ...prev,
        sendEvent: {
          done: true,
          failed: false,
          status: response.status_code_with_hamc,
          response: response.response_with_hmac,
        },
      }));

      await new Promise((r) => setTimeout(r, 400));

      setLoadingStep('positiveHmac');

      const positiveFailed = response.status_code_with_hamc !== 200;

      setSteps((prev) => ({
        ...prev,
        positiveHmac: {
          done: !positiveFailed,
          failed: positiveFailed,
          status: response.status_code_with_hamc,
          response: response.response_with_hmac,
        },
      }));

      await new Promise((r) => setTimeout(r, 400));

      setLoadingStep('negativeHmac');

      const negativeStatus = response.status_code_without_hamc;
      const negativeFailed = !(negativeStatus === 401 || negativeStatus === 403);

      setSteps((prev) => ({
        ...prev,
        negativeHmac: {
          done: !negativeFailed,
          failed: negativeFailed,
          status: response.status_code_without_hamc,
          response: response.response_without_hmac,
        },
      }));

      await new Promise((r) => setTimeout(r, 400));

      const hasFailure = positiveFailed || negativeFailed;

      setSteps((prev) => ({
        ...prev,
        finished: {
          done: !hasFailure,
          failed: hasFailure,
        },
      }));

      setCompleted(!hasFailure);
      setFailed(hasFailure);
      setLoadingStep(null);
      setTesting(false);

      if (hasFailure) {
        dispatch(
          showSnackbar({
            message: 'Webhook validation failed',
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: `Webhook test sent successfully to ${name}`,
            severity: 'success',
          }),
        );
      }
    } catch (err) {
      setFailed(true);
      setLoadingStep(null);
      setTesting(false);

      dispatch(
        showSnackbar({
          message: 'Webhook test failed',
          severity: 'error',
        }),
      );
    }
  };

  useEffect(() => {
    if (!hasRun.current && webhookId) {
      hasRun.current = true;
      runTest();
    }
  }, [webhookId]);

  return (
    <>
      {completed ? (
        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
          <CheckCircleIcon color="success" />
          <Typography sx={{ color: '#22c55e', fontWeight: 600 }}>Webhook test completed for {name}</Typography>
        </Stack>
      ) : failed ? (
        <Typography sx={{ color: '#ef4444', fontWeight: 600, mb: 3 }}>Webhook security validation failed</Typography>
      ) : (
        <Stack direction="row" spacing={1} alignItems="center" mb={3}>
          <CircularProgress size={18} />
          <Typography sx={{ color: '#9ca3af' }}>Testing webhook: {name}...</Typography>
        </Stack>
      )}

      <Stack spacing={2}>
        <TestWebhookSteps
          label="Sending test event"
          completed={steps.sendEvent.done}
          failed={steps.sendEvent.failed}
          loading={loadingStep === 'sendEvent'}
        />

        <TestWebhookError
          open={steps.sendEvent.failed}
          status={steps.sendEvent.status}
          response={steps.sendEvent.response}
          expectation="The webhook endpoint should accept the test event request."
        />

        <TestWebhookSteps
          label="Testing for positive HMAC"
          completed={steps.positiveHmac.done}
          failed={steps.positiveHmac.failed}
          loading={loadingStep === 'positiveHmac'}
        />

        <TestWebhookError
          open={steps.positiveHmac.failed}
          status={steps.positiveHmac.status}
          response={steps.positiveHmac.response}
          expectation="The endpoint should accept requests containing a valid HMAC signature, returning an HTTP 200 OK response."
        />

        <TestWebhookSteps
          label="Testing for negative HMAC"
          completed={steps.negativeHmac.done}
          failed={steps.negativeHmac.failed}
          loading={loadingStep === 'negativeHmac'}
        />

        <TestWebhookError
          open={steps.negativeHmac.failed}
          status={steps.negativeHmac.status}
          response={steps.negativeHmac.response}
          expectation="The endpoint should reject requests with missing or invalid signatures, returning an HTTP 401 Unauthorized response."
          docsLink={`${appConfig.docsBaseUrl}/#generate-hmac-sha256-signature`}
        />

        <TestWebhookSteps
          label="Webhook successfully tested"
          completed={steps.finished.done}
          failed={steps.finished.failed}
        />
      </Stack>

      {(completed || failed) && (
        <Stack alignItems="center" mt={4}>
          <Button
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={runTest}
            sx={{
              borderColor: '#374151',
              color: '#e5e7eb',
            }}
          >
            Test Again
          </Button>
        </Stack>
      )}
    </>
  );
};

export default TestWebhookRunner;
